import { Controller } from "@hotwired/stimulus"
import { itemFromEvent, isFolder, controllerFor, toast, csrfToken } from "./cloud_helpers"

const TYPE = "application/x-cloud-items"

// Trascinamento di tessere (anche selezione multipla) su cartelle dell'albero,
// tessere-cartella e radice. Un rilascio di file di sistema su una cartella
// carica lì. I bersagli portano data-drop-folder-id ("" = radice).
export default class extends Controller {
  static values = { moveUrl: String }

  start(event) {
    const item = itemFromEvent(event)
    if (!item) return
    const select = this.select
    if (select && !select.isSelected(item)) select.selectOnly(item)
    const items = select ? select.selectedItems : [item]
    const ids = items.map((el) => el.dataset.itemId)
    this._draggedFolderIds = items.filter(isFolder).map((el) => el.dataset.dropFolderId)
    event.dataTransfer.setData(TYPE, JSON.stringify(ids))
    event.dataTransfer.setData("text/plain", items.map((el) => el.dataset.label).join("\n"))
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setDragImage(this.ghost(items), 16, 16)
    items.forEach((el) => el.classList.add("is-dragging"))
    this.element.classList.add("cloud--dragging")
  }

  end() {
    this.element.querySelectorAll(".is-dragging").forEach((el) => el.classList.remove("is-dragging"))
    this.element.classList.remove("cloud--dragging")
    this._ghost?.remove()
    this._draggedFolderIds = []
  }

  over(event) {
    const target = event.currentTarget
    const kind = this.kindOf(event)
    if (!kind) return
    const folderId = target.dataset.dropFolderId
    if (kind === "items" && !this.validTarget(folderId, target)) {
      target.classList.add("is-drop-invalid")
      return
    }
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = kind === "items" ? "move" : "copy"
    target.classList.add(kind === "items" ? "is-drop" : "is-drop-upload")
  }

  leave(event) {
    const target = event.currentTarget
    if (target.contains(event.relatedTarget)) return
    target.classList.remove("is-drop", "is-drop-upload", "is-drop-invalid")
  }

  async drop(event) {
    const target = event.currentTarget
    target.classList.remove("is-drop", "is-drop-upload", "is-drop-invalid")
    const kind = this.kindOf(event)
    if (!kind) return
    event.preventDefault()
    event.stopPropagation()
    const folderId = target.dataset.dropFolderId
    const folderName = target.querySelector(".cloud-rail__name, .cloud-item__name")?.textContent?.trim() || target.textContent.trim()

    if (kind === "files") {
      if (folderId === "") return
      return this.upload?.uploadTo(folderId, Array.from(event.dataTransfer.files), folderName)
    }

    if (!this.validTarget(folderId, target)) return
    let ids = []
    try { ids = JSON.parse(event.dataTransfer.getData(TYPE)) } catch { return }
    if (!ids.length) return
    const body = new FormData()
    ids.forEach((id) => body.append("ids[]", id))
    body.append("destination_folder_id", folderId)
    // La radice non è una cartella: solo le cartelle possono finire lì.
    if (folderId === "") {
      const folderIds = ids.filter((id) => id.startsWith("folder-")).map((id) => id.replace("folder-", ""))
      if (!folderIds.length) return
      await Promise.all(folderIds.map((id) => this.patchFolderRoot(id)))
      return window.Turbo.visit(window.location.href, { action: "replace" })
    }
    const response = await fetch(this.moveUrlValue, {
      method: "POST", body,
      headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" }
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) return toast(data.error || data.message || response.statusText, { kind: "error" })
    toast(data.message)
    window.Turbo.visit(window.location.href, { action: "replace" })
  }

  async patchFolderRoot(id) {
    const body = new FormData()
    body.append("folder[parent_id]", "")
    body.append("_method", "patch")
    return fetch(`/cloud/${id}`, { method: "POST", body, headers: { "X-CSRF-Token": csrfToken(), Accept: "text/vnd.turbo-stream.html" } })
  }

  // Una cartella non può cadere su sé stessa, su un discendente o su un elemento già dentro.
  validTarget(folderId, target) {
    if (folderId === undefined) return false
    if ((this._draggedFolderIds || []).includes(folderId)) return false
    if (target.closest("[data-cloud-item].is-dragging")) return false
    const node = target.closest(".cloud-rail__node")
    if (node && (this._draggedFolderIds || []).some((id) => node.closest(`#folder_${id}_node`))) return false
    return true
  }

  kindOf(event) {
    const types = Array.from(event.dataTransfer?.types || [])
    if (types.includes(TYPE)) return "items"
    if (types.includes("Files")) return "files"
    return null
  }

  ghost(items) {
    const el = document.createElement("div")
    el.className = "cloud-drag-ghost"
    const first = items[0]
    el.innerHTML = `<span class="cloud-drag-ghost__name"></span>${items.length > 1 ? `<span class="cloud-drag-ghost__count">${items.length}</span>` : ""}`
    el.querySelector(".cloud-drag-ghost__name").textContent = first.dataset.label
    document.body.appendChild(el)
    this._ghost = el
    return el
  }

  get select() { return controllerFor(this.element, "cloud-select") }
  get upload() { return controllerFor(this.element, "cloud-upload") }
}
